/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, type ReactNode } from 'react';
import { GTMContext } from '../../../contexts/GTMContext';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import CropFreeSharpIcon from '@mui/icons-material/CropFreeSharp';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { GTM_EVENT_ZOOM } from '../../../constants/GTMEventNames';

type DisplayZoomProps = {
	shouldZoom: boolean;
	handleOnZoomClick: ( shouldZoom: boolean ) => void;
	isFullscreen: boolean;
};

export function DisplayZoom( { shouldZoom, handleOnZoomClick, isFullscreen }: DisplayZoomProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );

	function handleToggleButtonChange( event: React.MouseEvent, value: boolean ) {
		if ( value === null ) {
			return;
		}

		handleOnZoomClick( value );

		triggerGTMEvent( {
			event: GTM_EVENT_ZOOM,
			action: shouldZoom ? 'real' : 'auto'
		} );
	}

	return (
		<Box display={ 'flex' } alignItems={ 'center' } gap={ '2px' }>
			<Typography fontWeight={ 400 } fontSize={ '12px' } color={ 'text.secondary' } marginRight={ '6px' }>
				Editor view:
			</Typography>

			<ToggleButtonGroup
				value={ shouldZoom }
				exclusive
				onChange={ handleToggleButtonChange }
				color='primary'
				size='small'
				disabled={ isFullscreen }
				sx={ {
					height: '24px',
					color: 'text.secondary',
					backgroundColor: 'white',
					'& .MuiButtonBase-root': {
						height: '24px',
						fontSize: '14px',
						fontWeight: '400',
						'&.Mui-selected': {
							backgroundColor: 'white',
							borderColor: 'primary.main',
							cursor: 'default',
							'&:hover': {
								backgroundColor: 'white'
							}
						},
						'&.Mui-disabled.Mui-selected': {
							backgroundColor: 'primary.50',
							borderColor: 'primary.light',
							color: 'primary.400',
							cursor: 'default'
						}
					}
				} }
			>
				<Tooltip
					title={ isFullscreen ?
						'Zoom controls are disabled in fullscreen mode.' :
						'Automatically adjusts editor size for optimal visibility at lower resolutions.'
					}
				>
					<span>
						<ToggleButton value={ true }>
							<CropFreeSharpIcon style={ { width: '13px', marginRight: '4px' } }/>
							Auto resize
						</ToggleButton>
					</span>
				</Tooltip>
				<Tooltip
					title={ isFullscreen ?
						'Zoom controls are disabled in fullscreen mode.' :
						'Displays the editor at full size regardless of screen resolution.'
					}
				>
					<span>
						<ToggleButton value={ false }>
							<ZoomOutMapIcon style={ { width: '13px', marginRight: '4px' } }/>
							Real size
						</ToggleButton>
					</span>
				</Tooltip>
			</ToggleButtonGroup>
		</Box>
	);
}
